import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

const MIN_COMMENT = 10;

export function SystemRatingModal() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submittedRef = useRef(false);
  const dismissedThisVisitRef = useRef(false);

  useEffect(() => {
    if (!user) return;
    // Reset de visita ao trocar de usuário
    dismissedThisVisitRef.current = false;
    submittedRef.current = false;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc("should_show_rating_modal", { _user_id: user.id });
      if (cancelled) return;
      if (!error && data === true) setOpen(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const canSubmit = rating > 0 && comment.trim().length >= MIN_COMMENT && !submitting;

  const handleSubmit = async () => {
    if (!user || !canSubmit) return;
    setSubmitting(true);
    const sentiment = rating >= 4 ? "positive" : rating === 3 ? "neutral" : "negative";
    const { error } = await supabase.from("system_ratings").insert({
      user_id: user.id,
      rating,
      comment: comment.trim(),
      sentiment,
    });
    setSubmitting(false);
    if (error) {
      toast({ title: "Erro ao enviar avaliação", description: error.message, variant: "destructive" });
      return;
    }
    submittedRef.current = true;
    toast({ title: "Obrigado pelo seu feedback!", description: "Sua avaliação foi registrada com sucesso." });
    setOpen(false);
  };

  const handleOpenChange = async (next: boolean) => {
    if (next) {
      setOpen(true);
      return;
    }
    // Fechando sem enviar: pausa nesta visita; reaparece no próximo login
    if (!submittedRef.current && user) {
      dismissedThisVisitRef.current = true;
      void supabase.rpc("dismiss_rating_modal");
    }
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Como está sendo sua experiência?</DialogTitle>
          <DialogDescription>
            Sua opinião nos ajuda a melhorar a plataforma. Avalie de 1 a 5 estrelas e deixe um comentário.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-center justify-center gap-1.5">
            {[1, 2, 3, 4, 5].map((n) => {
              const active = (hover || rating) >= n;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  className="p-1 transition-transform hover:scale-110"
                  aria-label={`${n} estrela${n > 1 ? "s" : ""}`}
                >
                  <Star
                    className={cn(
                      "h-10 w-10 transition-colors",
                      active ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/40"
                    )}
                  />
                </button>
              );
            })}
          </div>

          <div className="space-y-1.5">
            <Textarea
              placeholder="Conte o que você está achando do sistema..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              maxLength={1000}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>
                {comment.trim().length < MIN_COMMENT
                  ? `Mínimo ${MIN_COMMENT} caracteres`
                  : "Comentário válido ✓"}
              </span>
              <span>{comment.length}/1000</span>
            </div>
          </div>

          <Button onClick={handleSubmit} disabled={!canSubmit} className="w-full">
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Enviando...
              </>
            ) : (
              "Enviar avaliação"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
